import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useStrict } from 'mobx';
import { Provider } from 'mobx-react';

import HttpClient from './infrastructure/adapter/HttpClient';
import CookieHandler from './infrastructure/adapter/CookieHandler';
import BrowserHistoryHandler from './infrastructure/adapter/BrowserHistoryHandler';
import { ZopimHandler } from './infrastructure/adapter/ZopimHandler';

import CurrentUserRepository from './domain/repository/CurrentUserRepository';
import ExclusionListRepository from './domain/repository/ExclusionListRepository';
import { KaizenLabServiceRepository } from './domain/repository/KaizenLabServiceRepository';
import OrganizationRepository from './domain/repository/OrganizationRepository';
import ProjectRepository from './domain/repository/ProjectRepository';

import CurrentUserStore from './domain/store/CurrentUserStore';
import ExclusionListStore from './domain/store/ExclusionListStore';
import { KaizenLabServiceStore } from './domain/store/KaizenLabServiceStore';
import OrganizationStore from './domain/store/OrganizationStore';
import ProjectStore from './domain/store/ProjectStore';
import UiState from './domain/store/UiState';
import { IStore } from './domain/store/IStore';

import applyGlobalStyles from './presentation/applyGlobalStyles';
import App from './presentation/container/App';

import { AuthenticationUsecase } from './application/usecase/AuthenticationUsecase';
import { ExclusionListUsecase } from './application/usecase/ExclusionListUsecase';
import { HistoryHandlerUsecase } from './application/usecase/HistoryHandlerUsecase';
import { KaizenLabServiceUsecase } from './application/usecase/KaizenLabServiceUsecase';
import { OrganizationUsecase } from './application/usecase/OrganizationUsecase';
import { ProjectUsecase } from './application/usecase/ProjectUsecase';
import { IUsecase } from './application/usecase/IUsecase';

import { Application } from './Application';
import config from './config';

if ((window as any).Bugsnag) {
  if (config.bugsnag) {
    (window as any).Bugsnag.releaseStage = config.bugsnagReleaseStage || 'development';
  } else {
    (window as any).Bugsnag.beforeNotify = () => false;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // instantiate
  const httpClient = new HttpClient({ baseUrl: config.api_base });
  const cookieHandler = new CookieHandler();
  const browserHistoryHandler = new BrowserHistoryHandler();
  const zopimHandler = new ZopimHandler();

  const currentUserRepository = new CurrentUserRepository({ httpClient, cookieHandler, browserHistoryHandler });
  const exclusionListRepository = new ExclusionListRepository({
    httpClient,
    cookieHandler,
    browserHistoryHandler,
  });
  const kaizenLabServiceRepository = new KaizenLabServiceRepository({
    httpClient,
    cookieHandler,
    browserHistoryHandler,
  });
  const organizationRepository = new OrganizationRepository({ httpClient, cookieHandler, browserHistoryHandler });
  const projectRepository = new ProjectRepository({ httpClient, cookieHandler, browserHistoryHandler });

  const currentUserStore = new CurrentUserStore();
  const exclusionListStore = new ExclusionListStore();
  const kaizenLabServiceStore = new KaizenLabServiceStore();
  const organizationStore = new OrganizationStore();
  const projectStore = new ProjectStore();
  const uiState = new UiState();

  const authenticationUsecase = new AuthenticationUsecase({ currentUserRepository, currentUserStore, zopimHandler });
  const exclusionListUsecase = new ExclusionListUsecase({ exclusionListRepository, exclusionListStore });
  const historyHandlerUsecase = new HistoryHandlerUsecase({ browserHistoryHandler });
  const kaizenLabServiceUsecase = new KaizenLabServiceUsecase({
    kaizenLabServiceRepository,
    kaizenLabServiceStore,
    uiState,
  });
  const organizationUsecase = new OrganizationUsecase({
    organizationRepository,
    organizationStore,
    uiState,
  });
  const projectUsecase = new ProjectUsecase({ projectRepository, organizationStore, projectStore, uiState });

  const application = new Application({
    authenticationUsecase,
    exclusionListUsecase,
    historyHandlerUsecase,
    kaizenLabServiceUsecase,
    organizationUsecase,
    projectUsecase,
    organizationStore,
    projectStore,
    uiState,
  });

  const store: IStore = {
    currentUserStore,
    exclusionListStore,
    kaizenLabServiceStore,
    organizationStore,
    projectStore,
    uiState,
  };

  const usecase: IUsecase = {
    exclusionListUsecase,
    projectUsecase,
  };

  // Use MobX in strict mode
  useStrict(true);

  applyGlobalStyles();

  // boot up 👢
  application.boot();

  ReactDOM.render(
    <Provider store={store} usecase={usecase}>
      <App />
    </Provider>,
    document.getElementById('root') as HTMLElement,
  );

  // Setup zopim
  if (config.zopim) {
    zopimHandler.addTags('round-monitor');
    zopimHandler.minimize();
  } else {
    zopimHandler.hideAll();
  }
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/index.tsx